import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Trophy, Loader2 } from "lucide-react";
import { useApiResource } from "../api/useApi";
import { getRankings } from "../api/resources";
import type { RankingItem, RankingsSort } from "../api/resources";
import { ACTIVITIES, ACTIVITY_LABEL, ActivityId } from "../mockData";
import { TierBadge, TIER_LABELS } from "../components/TierBadge";
import { FilterCard, ActivityMultiFilter } from "../components/VisualFilter";
import { TierFilterRow, AthleteFilterRow, parseAthletesParam, encodeAthletesParam } from "../components/ListFilters";

/**
 * Global athlete rankings — the public face of the biomechanics database. Every
 * authenticated user sees the same list (platform/rankings is not owner-gated).
 *
 * rankings-pagination: THE DEFAULT VIEW is recent-first (the user: "show the most recent
 * upload first"), server-paged with Load-more accumulation — the same idiom and page size
 * as the sessions list. The rank on each row is the server's score-rank over the full
 * filtered set: recent sort reorders, never renumbers, so a recently-active low-ranked
 * athlete leads the list still wearing their real "#14".
 *
 * The technique selector refetches SERVER-side (technique=) rather than re-ranking
 * locally: under paging the client only holds one page of hybrid-ordered rows, which
 * cannot contain a technique's true leaders. Display values still come from the row's
 * perTechnique breakdown; the rank is the server's technique score-rank. Options come from
 * the static ACTIVITIES catalogue (the loaded page no longer sees every technique), plus
 * any sportId present in loaded rows that the catalogue doesn't know.
 *
 * Sparse-data note: scores come from rule scorecards, which only exist for uploads
 * analyzed since rule scoring landed. Unranked athletes (no scored clips yet) show with
 * clips/last-analyzed visible; the library re-render backfills them.
 */

// Same server-side page size as the sessions list (Sessions.tsx PAGE_SIZE).
const PAGE_SIZE = 10;

const fmtScore = (v?: number | null) => (v == null ? "—" : v.toFixed(1));
const fmtDate = (iso?: string | null) => (iso ? iso.slice(0, 10) : "—");

type ViewRow = {
  key: number;
  rank: number;
  name: string;
  tier?: string | null;
  avg?: number | null;
  latest?: number | null;
  clips: number;
  lastAnalyzed?: string | null;
};

export default function Rankings() {
  const navigate = useNavigate();

  // ALL filter state lives in the URL (same convention as Sessions), so returning to the
  // page or sharing the link restores the exact view. Multi-entry filters are comma-joined.
  const [searchParams, setSearchParams] = useSearchParams();
  // Score IS the default sort (user direction 2026-08-27: "it should be by score").
  const sort: RankingsSort = searchParams.get("sort") === "recent" ? "recent" : "score";
  const technique = searchParams.get("technique") ?? "all";
  const sports = useMemo(
    () => (searchParams.get("sports") ?? "").split(",").filter(Boolean),
    [searchParams]
  );
  const tiers = useMemo(
    () => (searchParams.get("tiers") ?? "").split(",").filter(Boolean),
    [searchParams]
  );
  const athletes = useMemo(
    () => parseAthletesParam(searchParams.get("athletes")),
    [searchParams]
  );

  // Server-side pagination with client-side accumulation (the Sessions idiom): fetch a
  // page at a time and append, so "Load more" grows the list without re-fetching.
  const [page, setPage] = useState(1);
  const [allRows, setAllRows] = useState<RankingItem[]>([]);
  const [totalCount, setTotalCount] = useState<number | null>(null);

  // Any change of view restarts paging from page 1.
  const resetPaging = () => {
    setPage(1);
    setAllRows([]);
    setTotalCount(null);
  };

  // Single writer for the URL-persisted filter state (replace, not push — chip toggles
  // must not bury the history stack).
  const setListParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (value == null || value === "" || value === "all") next.delete(key);
    else next.set(key, value);
    setSearchParams(next, { replace: true });
    resetPaging();
  };

  const toggleTier = (tier: string) => {
    const next = tiers.includes(tier) ? tiers.filter((t) => t !== tier) : [...tiers, tier];
    setListParam("tiers", next.length > 0 ? next.join(",") : null);
  };

  const setAthletes = (next: { id: number; name: string }[]) =>
    setListParam("athletes", next.length > 0 ? encodeAthletesParam(next) : null);

  // Sport families -> the activity-id strings scorecards carry ("golfSwingDTL", ...).
  const sportActivityIds = useMemo(
    () => ACTIVITIES.filter((a) => sports.includes(a.group)).map((a) => a.id as string),
    [sports]
  );

  const { data, isLoading, error } = useApiResource(
    (apiFetch) =>
      getRankings(apiFetch, {
        tiers: tiers.length > 0 ? tiers : undefined,
        sports: sportActivityIds.length > 0 ? sportActivityIds : undefined,
        contactIds: athletes.length > 0 ? athletes.map((a) => a.id) : undefined,
        sort,
        technique: technique === "all" ? undefined : technique,
        page,
        pageSize: PAGE_SIZE,
      }),
    [tiers.join(","), sports.join(","), athletes.map((a) => a.id).join(","), technique, sort, page]
  );

  // Merge each loaded page into the accumulator, deduping by contactId so a re-fired
  // effect (or a reload of the same page) can't add the same athlete twice.
  useEffect(() => {
    if (!data) return;
    const vals = data.values ?? [];
    setAllRows((prev) => {
      if (page === 1) return vals;
      const seen = new Set(prev.map((r) => r.contactId));
      return [...prev, ...vals.filter((v) => !seen.has(v.contactId))];
    });
    if (data.totalCount != null) setTotalCount(data.totalCount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const techniques = useMemo(() => {
    const ids = new Set<string>(ACTIVITIES.map((a) => a.id as string));
    allRows.forEach((r) => r.perTechnique?.forEach((t) => t.sportId && ids.add(t.sportId)));
    return Array.from(ids).sort();
  }, [allRows]);

  const rows: ViewRow[] = useMemo(() => {
    if (technique === "all") {
      // Server order IS the view: recent or score, server-sorted; rank rendered verbatim.
      return allRows.map((r) => ({
        key: r.contactId,
        rank: r.rank,
        name: r.name || `Athlete #${r.contactId}`,
        tier: r.tier,
        avg: r.hybridScore,
        latest: r.latestScore,
        clips: r.clipCount,
        lastAnalyzed: r.lastAnalyzedUtc,
      }));
    }
    // Technique view: the server already filtered, ranked and sorted — this is purely a
    // projection of each row's perTechnique entry. NO client-side re-ranking.
    return allRows
      .map((r) => ({ r, t: r.perTechnique?.find((t) => t.sportId === technique) }))
      .filter((x): x is { r: RankingItem; t: NonNullable<typeof x.t> } => x.t != null)
      .map(({ r, t }) => ({
        key: r.contactId,
        rank: r.rank,
        name: r.name || `Athlete #${r.contactId}`,
        tier: r.tier,
        avg: t.averageScore,
        latest: t.latestScore,
        clips: t.clipCount,
        lastAnalyzed: t.lastAnalyzedUtc,
      }));
  }, [allRows, technique]);

  const hasMore = totalCount != null && allRows.length < totalCount;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Trophy className="h-5 w-5 text-el-600" />
          <div>
            <div className="text-lg font-semibold text-slate-900">Rankings</div>
            <div className="text-xs text-slate-500">
              The global athlete database — every analyzed clip feeds it.
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Recent (default) | Top ranked — server-side sort; the rank column never changes. */}
          <div className="flex rounded-md border border-slate-200 bg-white p-0.5" role="group" aria-label="Sort order">
            <button
              type="button"
              data-testid="sort-score"
              onClick={() => {
                if (sort === "score") return;
                setListParam("sort", null);
              }}
              className={`rounded px-2.5 py-1 text-xs font-medium transition ${
                sort === "score" ? "bg-el-50 text-el-800" : "text-slate-600 hover:bg-slate-50"
              }`}
            >
              Top ranked
            </button>
            <button
              type="button"
              data-testid="sort-recent"
              onClick={() => {
                if (sort === "recent") return;
                setListParam("sort", "recent");
              }}
              className={`rounded px-2.5 py-1 text-xs font-medium transition ${
                sort === "recent" ? "bg-el-50 text-el-800" : "text-slate-600 hover:bg-slate-50"
              }`}
            >
              Recent
            </button>
          </div>

          <select
            data-testid="technique-select"
            value={technique}
            onChange={(e) => setListParam("technique", e.target.value)}
            className="h-8 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700 focus:outline-none focus:shadow-ring"
            title="Scores are only directly comparable within a technique"
          >
            <option value="all">All techniques</option>
            {techniques.map((id) => (
              <option key={id} value={id}>
                {ACTIVITY_LABEL[id as ActivityId] ?? id}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* The SHARED multi-entry filter set (same components as the sessions list). */}
      <FilterCard
        countActive={
          (sports.length > 0 ? 1 : 0) + (tiers.length > 0 ? 1 : 0) + (athletes.length > 0 ? 1 : 0)
        }
        summary={[
          { label: "Activity", value: sports.length === 0 ? "All" : sports.join(", "),
            onClear: sports.length > 0 ? () => setListParam("sports", null) : undefined },
          { label: "Tier",
            value: tiers.length === 0 ? "All" : tiers.map((t) => TIER_LABELS[t] ?? t).join(", "),
            onClear: tiers.length > 0 ? () => setListParam("tiers", null) : undefined },
          ...(athletes.length > 0
            ? [{ label: "Athlete", value: athletes.map((a) => a.name).join(", "),
                onClear: () => setAthletes([]) }]
            : []),
        ]}
      >
        <ActivityMultiFilter
          values={sports}
          onChange={(next) => setListParam("sports", next.length > 0 ? next.join(",") : null)}
        />
        <TierFilterRow
          tiers={tiers}
          onToggle={toggleTier}
          onClear={() => setListParam("tiers", null)}
          hint="the athlete's current tier — pick several"
        />
        <AthleteFilterRow
          athletes={athletes}
          onAdd={(a) => {
            if (!athletes.some((x) => x.id === a.id)) setAthletes([...athletes, a]);
          }}
          onRemove={(id) => setAthletes(athletes.filter((a) => a.id !== id))}
          label="Athlete"
          hint="jump to specific athletes — their rank stays real"
        />
      </FilterCard>

      {isLoading && allRows.length === 0 && (
        <div
          data-testid="rankings-loading"
          className="flex items-center gap-2 justify-center py-12 text-sm text-slate-500"
        >
          <Loader2 className="h-5 w-5 animate-spin text-el-600" /> Loading rankings…
        </div>
      )}

      {!isLoading && error && (
        <div className="text-sm text-rose-600 py-8 text-center">
          Couldn't load the rankings — try again.
        </div>
      )}

      {!isLoading && !error && rows.length === 0 && (
        <div data-testid="rankings-empty" className="text-center py-12 space-y-1">
          <div className="text-sm font-medium text-slate-700">No ranked athletes yet.</div>
          <div className="text-xs text-slate-500 max-w-md mx-auto">
            Rankings come from rule scorecards, which only exist for clips analyzed since
            rule scoring landed. Upload and analyze clips — or re-analyze older ones — and
            athletes will appear here.
          </div>
        </div>
      )}

      {rows.length > 0 && (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[0.7rem] uppercase tracking-wide text-slate-400 border-b border-slate-100">
                <th className="px-4 py-2.5 w-12">#</th>
                <th className="px-4 py-2.5">Athlete</th>
                <th className="px-4 py-2.5">Tier</th>
                <th className="px-4 py-2.5 text-right">Avg score</th>
                <th className="px-4 py-2.5 text-right">Latest</th>
                <th className="px-4 py-2.5 text-right">Clips</th>
                <th className="px-4 py-2.5 text-right">Last analyzed</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.key}
                  data-testid="ranking-row"
                  onClick={() => navigate(`/athletes/${row.key}`)}
                  title="Open this athlete's clip gallery"
                  className="border-b border-slate-50 last:border-0 hover:bg-slate-50/60 transition cursor-pointer"
                >
                  <td className="px-4 py-2.5 font-semibold text-slate-500" data-testid="ranking-rank">
                    {row.rank > 0 ? row.rank : "—"}
                  </td>
                  <td className="px-4 py-2.5 font-medium text-slate-800">{row.name}</td>
                  <td className="px-4 py-2.5">
                    <TierBadge tier={row.tier} showUnranked />
                  </td>
                  <td className="px-4 py-2.5 text-right font-semibold text-slate-800" data-testid="ranking-avg">
                    {fmtScore(row.avg)}
                  </td>
                  <td className="px-4 py-2.5 text-right text-slate-600" data-testid="ranking-latest">
                    {fmtScore(row.latest)}
                  </td>
                  <td className="px-4 py-2.5 text-right text-slate-600" data-testid="ranking-clips">
                    {row.clips}
                  </td>
                  <td className="px-4 py-2.5 text-right text-slate-500">{fmtDate(row.lastAnalyzed)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {hasMore && (
        <div className="flex justify-center pt-1">
          <button
            data-testid="rankings-load-more"
            onClick={() => setPage((p) => p + 1)}
            disabled={isLoading}
            className="btn-secondary !h-9"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Loading…
              </>
            ) : (
              <>
                Load more · {allRows.length} of {totalCount}
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}
